import type { InfluencerCall } from "@/types/influencer";

export interface Position {
  asset: string;
  action: "LONG" | "SHORT" | "WATCH" | "HOLD";
  entryPrice: number;
  exitPrice: number | null;
  currentPrice: number | null;
  entryDate: string;
  exitDate: string | null;
  status: "OPEN" | "CLOSED";
  pnlPercent: number | null;
  pnlPerDay: number | null;
  calls: InfluencerCall[]; // all raw calls in this position
  entryPriceSourceInterval: string | null;
  entryPriceSourceTimestamp: string | null;
  exitPriceSourceInterval: string | null;
  exitPriceSourceTimestamp: string | null;
}

/**
 * Groups raw calls into positions.
 *
 * Logic:
 * - Sort calls by date ascending per asset
 * - A WATCH or HOLD can open a position with action "WATCH" (no PnL entry yet)
 * - A BUY opens/upgrades to a LONG position (entry = price at that BUY call)
 * - A SHORT opens/upgrades to a SHORT position
 * - Subsequent BUY/HOLD calls on the same asset while a position is open are ignored for PnL
 * - A SELL closes the current LONG position: PnL = (sellPrice - entryPrice) / entryPrice
 * - A BUY closes a SHORT position: PnL = (entryPrice - buyPrice) / entryPrice
 * - If no closing call, mark-to-market using currentPrice from the opening call
 * - WATCH positions that never get a BUY/SHORT have null PnL
 */
export function groupCallsIntoPositions(calls: InfluencerCall[]): Position[] {
  // Group by asset
  const byAsset = new Map<string, InfluencerCall[]>();
  for (const call of calls) {
    const list = byAsset.get(call.asset) || [];
    list.push(call);
    byAsset.set(call.asset, list);
  }

  const positions: Position[] = [];

  for (const [asset, assetCalls] of byAsset) {
    // Sort by date ascending
    const sorted = [...assetCalls].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    );

    let current: {
      action: "LONG" | "SHORT" | "WATCH" | "HOLD";
      entryPrice: number;
      entryDate: string;
      entryPriceSourceInterval: string | null;
      entryPriceSourceTimestamp: string | null;
      calls: InfluencerCall[];
      latestCurrentPrice: number | null;
    } | null = null;

    for (const call of sorted) {
      if (!current) {
        // No open position — open one
        if (call.action === "BUY") {
          current = {
            action: "LONG",
            entryPrice: call.priceAtCall ?? 0,
            entryDate: call.date,
            entryPriceSourceInterval: call.priceSourceInterval,
            entryPriceSourceTimestamp: call.priceSourceTimestamp,
            calls: [call],
            latestCurrentPrice: call.currentPrice,
          };
        } else if (call.action === "SHORT") {
          current = {
            action: "SHORT",
            entryPrice: call.priceAtCall ?? 0,
            entryDate: call.date,
            entryPriceSourceInterval: call.priceSourceInterval,
            entryPriceSourceTimestamp: call.priceSourceTimestamp,
            calls: [call],
            latestCurrentPrice: call.currentPrice,
          };
        } else if (call.action === "WATCH") {
          current = {
            action: "WATCH",
            entryPrice: 0,
            entryDate: call.date,
            entryPriceSourceInterval: null,
            entryPriceSourceTimestamp: null,
            calls: [call],
            latestCurrentPrice: call.currentPrice,
          };
        } else if (call.action === "HOLD") {
          current = {
            action: "HOLD",
            entryPrice: 0,
            entryDate: call.date,
            entryPriceSourceInterval: null,
            entryPriceSourceTimestamp: null,
            calls: [call],
            latestCurrentPrice: call.currentPrice,
          };
        }
      } else {
        // Position is open
        current.calls.push(call);
        if (call.currentPrice !== null) {
          current.latestCurrentPrice = call.currentPrice;
        }

        // If position is WATCH or HOLD, upgrade on BUY/SHORT
        if (current.action === "WATCH" || current.action === "HOLD") {
          if (call.action === "BUY") {
            current.action = "LONG";
            current.entryPrice = call.priceAtCall ?? 0;
            current.entryDate = call.date;
            current.entryPriceSourceInterval = call.priceSourceInterval;
            current.entryPriceSourceTimestamp = call.priceSourceTimestamp;
          } else if (call.action === "SHORT") {
            current.action = "SHORT";
            current.entryPrice = call.priceAtCall ?? 0;
            current.entryDate = call.date;
            current.entryPriceSourceInterval = call.priceSourceInterval;
            current.entryPriceSourceTimestamp = call.priceSourceTimestamp;
          } else if (call.action === "SELL" || call.action === "CLOSE") {
            // Close WATCH/HOLD position with null PnL
             positions.push({
              asset,
              action: current.action,
              entryPrice: 0,
              exitPrice: call.priceAtCall ?? 0,
              currentPrice: current.latestCurrentPrice,
              entryDate: current.entryDate,
              exitDate: call.date,
              status: "CLOSED",
              pnlPercent: null,
              pnlPerDay: null,
              calls: current.calls,
              entryPriceSourceInterval: current.entryPriceSourceInterval,
              entryPriceSourceTimestamp: current.entryPriceSourceTimestamp,
              exitPriceSourceInterval: call.priceSourceInterval,
              exitPriceSourceTimestamp: call.priceSourceTimestamp,
            });
            current = null;
          }
          // WATCH/HOLD while already WATCH/HOLD: just keep accumulating
          continue;
        }

        // LONG or SHORT position — check for close
        // CLOSE always closes any position without re-opening
        const wouldClose =
          call.action === "CLOSE" ||
          (current.action === "LONG" && (call.action === "SELL" || call.action === "SHORT")) ||
          (current.action === "SHORT" && (call.action === "BUY"));
        // Guard: if closing call has no price yet (overnight/pre-market), keep position open
        const closes = wouldClose && call.priceAtCall !== null;

        if (closes) {
          const exitPrice = call.priceAtCall!;
          const entry = current.entryPrice;
          const pnl =
            entry > 0
              ? current.action === "LONG"
                ? ((exitPrice - entry) / entry) * 100
                : ((entry - exitPrice) / entry) * 100
              : null;

          const roundedPnl = pnl !== null ? Math.round(pnl * 100) / 100 : null;
          const daysHeld = Math.max(1, (new Date(call.date).getTime() - new Date(current.entryDate).getTime()) / (1000 * 60 * 60 * 24));
          const pnlPerDay = roundedPnl !== null ? Math.round((Math.pow(1 + roundedPnl / 100, 1 / daysHeld) - 1) * 100 * 100) / 100 : null;

          positions.push({
            asset,
            action: current.action,
            entryPrice: entry,
            exitPrice: exitPrice,
            currentPrice: current.latestCurrentPrice,
            entryDate: current.entryDate,
            exitDate: call.date,
            status: "CLOSED",
            pnlPercent: roundedPnl,
            pnlPerDay,
            calls: current.calls,
            entryPriceSourceInterval: current.entryPriceSourceInterval,
            entryPriceSourceTimestamp: current.entryPriceSourceTimestamp,
            exitPriceSourceInterval: call.priceSourceInterval,
            exitPriceSourceTimestamp: call.priceSourceTimestamp,
          });
          current = null;

          // If the closing call is itself directional, open a new position
          // CLOSE never re-opens a position
          if (call.action !== "CLOSE" && call.action === "SHORT") {
            current = {
              action: "SHORT",
              entryPrice: call.priceAtCall ?? 0,
              entryDate: call.date,
              entryPriceSourceInterval: call.priceSourceInterval,
              entryPriceSourceTimestamp: call.priceSourceTimestamp,
              calls: [call],
              latestCurrentPrice: call.currentPrice,
            };
          } else if (call.action !== "CLOSE" && call.action === "BUY") {
            current = {
              action: "LONG",
              entryPrice: call.priceAtCall ?? 0,
              entryDate: call.date,
              entryPriceSourceInterval: call.priceSourceInterval,
              entryPriceSourceTimestamp: call.priceSourceTimestamp,
              calls: [call],
              latestCurrentPrice: call.currentPrice,
            };
          }
        }
      }
    }

    // If position still open, mark to market
    if (current) {
      const entry = current.entryPrice;
      const mktPrice = current.latestCurrentPrice;
      const isNeutral = current.action === "WATCH" || current.action === "HOLD";
      const pnl =
        !isNeutral && entry > 0 && mktPrice !== null
          ? current.action === "LONG"
            ? ((mktPrice - entry) / entry) * 100
            : ((entry - mktPrice) / entry) * 100
          : null;

      const roundedPnl = pnl !== null ? Math.round(pnl * 100) / 100 : null;
      const daysHeld = Math.max(1, (Date.now() - new Date(current.entryDate).getTime()) / (1000 * 60 * 60 * 24));
      const pnlPerDay = roundedPnl !== null ? Math.round((Math.pow(1 + roundedPnl / 100, 1 / daysHeld) - 1) * 100 * 100) / 100 : null;

      positions.push({
        asset,
        action: current.action,
        entryPrice: entry,
        exitPrice: null,
        currentPrice: mktPrice,
        entryDate: current.entryDate,
        exitDate: null,
        status: "OPEN",
        pnlPercent: roundedPnl,
        pnlPerDay,
        calls: current.calls,
        entryPriceSourceInterval: current.entryPriceSourceInterval,
        entryPriceSourceTimestamp: current.entryPriceSourceTimestamp,
        exitPriceSourceInterval: null,
        exitPriceSourceTimestamp: null,
      });
    }
  }

  // Sort by entry date descending
  positions.sort((a, b) => new Date(b.entryDate).getTime() - new Date(a.entryDate).getTime());

  return positions;
}
